import React, { useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  KeyboardAvoidingView,
  Platform,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import {
  CommonActions,
  NavigationProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { getPendingInvitationToken, invitationRoute } from '../../navigation/pendingInvitation';

import { wipeSessionOnLogout } from '../../services/auth/logoutWipe';
import {
  fetchAndUnwrapMasterKey,
  InvalidSecretCodeError,
  NotEnrolledError,
} from '../../services/encryption/MasterKeyService';
import { SecureStorageError } from '../../services/storage/SecureStorage';
import { createLogger } from '../../utils/logger';
import { ms, s, vs } from '../../utils/responsive';

import eyeIcon from '../../assets/icons/Eye.png';
import eyeClosedIcon from '../../assets/icons/Eye-closed.png';

const logger = createLogger('UnlockVault');

const MIN_LEN = 8;

/**
 * Shown when the user has enrolled a recovery code but no unwrapped
 * master key is cached on this device — typically on a new install,
 * after a logout, or after keychain eviction.
 *
 * Takes the recovery code, calls MasterKeyService.fetchAndUnwrapMasterKey,
 * and on success routes the user to Dashboard. On failure (wrong code)
 * stays on the screen with an error. "Sign out" is the escape hatch for
 * someone who's forgotten their code and wants to start fresh — this
 * does NOT recover their data (we can't); it just logs them out.
 */
const UnlockVaultScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp<any>>();
  // Set when an in-app action (playing a recording, say) sent the user
  // here; after unlocking they go back to it instead of Dashboard.
  const route = useRoute<any>();
  const returnTo = route.params?.returnTo as
    | { root: string; routes: { name: string; params?: object }[] }
    | undefined;
  const [code, setCode] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);

  const canSubmit = code.length >= MIN_LEN && !submitting;

  const routeToDashboard = async () => {
    // An invitation opened before sign-in comes first, unless the unlock
    // was asked for by a screen that wants the visitor back.
    if (!(returnTo && returnTo.routes.length > 0)) {
      const invitationToken = await getPendingInvitationToken();
      if (invitationToken) {
        navigation.dispatch(CommonActions.reset(invitationRoute(invitationToken)));
        return;
      }
    }
    if (returnTo && returnTo.routes.length > 0) {
      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [
            {
              name: returnTo.root,
              state: { index: returnTo.routes.length - 1, routes: returnTo.routes },
            },
          ],
        }),
      );
      return;
    }
    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: 'Dashboard' }],
      }),
    );
  };

  const handleUnlock = async () => {
    setErrorMessage(null);
    setSubmitting(true);
    try {
      await fetchAndUnwrapMasterKey(code);
      logger.info('Vault unlocked');
      routeToDashboard();
    } catch (err) {
      if (err instanceof InvalidSecretCodeError) {
        setAttempts((n) => n + 1);
        setErrorMessage(
          'Wrong recovery code. Check the letters exactly — this is case-sensitive.',
        );
      } else if (err instanceof NotEnrolledError) {
        // Shouldn't happen — we only navigate here if we've confirmed
        // enrollment. Treat as a soft state and send the user to
        // Dashboard with the legacy path.
        logger.warn('NotEnrolledError on unlock screen — routing through');
        routeToDashboard();
      } else if (err instanceof SecureStorageError) {
        setErrorMessage(
          'Your recovery code was accepted, but this device could not save the vault key. Make sure Face ID, Touch ID, or a device passcode is enabled, then try again.',
        );
      } else {
        logger.error('Unlock failed');
        setErrorMessage(
          err instanceof Error
            ? err.message
            : 'Could not unlock. Please try again.',
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = () => {
    Alert.alert(
      'Sign out?',
      'This signs you out of the app. If you have forgotten your recovery code, signing out does NOT recover your data — only you have the code.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Sign out',
          style: 'destructive',
          onPress: async () => {
            try {
              await wipeSessionOnLogout();
            } catch (e) {
              logger.error('wipeSessionOnLogout failed');
            }
            navigation.dispatch(
              CommonActions.reset({
                index: 0,
                routes: [
                  {
                    name: 'Auth',
                    state: { routes: [{ name: 'Login' }] },
                  },
                ],
              }),
            );
          },
        },
      ],
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={styles.content}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.badge}>
            <Text style={styles.badgeEmoji}>🔐</Text>
          </View>

          <Text style={styles.title}>Unlock your vault</Text>
          <Text style={styles.description}>
            Enter the recovery code you set when you turned on vault encryption.
            It's the same code every time — if you don't remember it, check
            wherever you wrote it down.
          </Text>

          <View style={styles.inputRow}>
            <TextInput
              style={styles.input}
              placeholder="Recovery code"
              placeholderTextColor="#9A9A9A"
              value={code}
              onChangeText={setCode}
              secureTextEntry={!showCode}
              autoCapitalize="none"
              autoCorrect={false}
              autoFocus
              textContentType="password"
              accessibilityLabel="Recovery code"
            />
            <TouchableOpacity
              style={styles.eyeToggle}
              onPress={() => setShowCode((v) => !v)}
              accessibilityRole="button"
              accessibilityLabel={showCode ? 'Hide code' : 'Show code'}
            >
              <Image
                source={showCode ? eyeIcon : eyeClosedIcon}
                style={styles.eyeIcon}
              />
            </TouchableOpacity>
          </View>

          {errorMessage ? (
            <Text style={styles.error} accessibilityRole="alert">
              {errorMessage}
              {attempts >= 3 ? ' If you can\'t remember it, use Sign out below.' : ''}
            </Text>
          ) : null}

          <TouchableOpacity
            style={[
              styles.primaryButton,
              !canSubmit && styles.primaryButtonDisabled,
            ]}
            onPress={handleUnlock}
            disabled={!canSubmit}
            accessibilityRole="button"
          >
            {submitting ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text style={styles.primaryButtonText}>Unlock</Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.signOutButton}
            onPress={handleSignOut}
            accessibilityRole="button"
          >
            <Text style={styles.signOutText}>Sign out instead</Text>
          </TouchableOpacity>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  content: {
    padding: s(24),
    paddingTop: vs(60),
    alignItems: 'center',
  },
  badge: {
    width: ms(80),
    height: ms(80),
    borderRadius: ms(40),
    backgroundColor: '#E5F7F7',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: vs(24),
  },
  badgeEmoji: {
    fontSize: ms(38),
  },
  title: {
    fontSize: ms(24),
    fontWeight: '700',
    color: '#1A1A1A',
    marginBottom: vs(10),
    textAlign: 'center',
  },
  description: {
    fontSize: ms(14),
    color: '#5A5A5A',
    lineHeight: ms(22),
    textAlign: 'center',
    marginBottom: vs(28),
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D8DBDD',
    borderRadius: ms(8),
    paddingHorizontal: s(12),
    width: '100%',
    marginBottom: vs(12),
  },
  input: {
    flex: 1,
    fontSize: ms(15),
    color: '#1A1A1A',
    paddingVertical: vs(14),
  },
  eyeToggle: {
    padding: s(8),
  },
  eyeIcon: {
    width: ms(20),
    height: ms(20),
    tintColor: '#666',
  },
  error: {
    fontSize: ms(13),
    color: '#E85C5C',
    marginBottom: vs(12),
    marginTop: vs(4),
    alignSelf: 'flex-start',
    lineHeight: ms(20),
  },
  primaryButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: vs(14),
    borderRadius: ms(8),
    alignItems: 'center',
    marginTop: vs(8),
    width: '100%',
  },
  primaryButtonDisabled: {
    backgroundColor: '#B5E3E3',
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16),
    fontWeight: '600',
  },
  signOutButton: {
    paddingVertical: vs(14),
    marginTop: vs(8),
  },
  signOutText: {
    color: '#5A5A5A',
    fontSize: ms(14),
    textDecorationLine: 'underline',
  },
});

export default UnlockVaultScreen;
